import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartLineUp,
  Warning,
  CheckCircle,
  ArrowRight,
  TrendUp,
  TrendDown,
  Target,
  Question,
  CaretDown,
  Eye,
  Lightbulb,
  Calculator,
  Scales,
  ChartBar,
  Percent,
  CurrencyCircleDollar,
  ArrowsClockwise,
  Funnel,
  Lightning,
  Crosshair,
  MagnifyingGlass,
  Gear
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const ProfitFactorTrading = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'What Is Profit Factor in Trading | Complete Guide',
      description: 'Learn what profit factor is in trading, how to calculate it, and how to use it to improve your trading performance.',
      path: '/profit-factor-trading',
      lang: 'en',
      alternates: getArticleAlternates('/profit-factor-trading'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  const [openFaq, setOpenFaq] = React.useState(null);

  const faqs = [
    {
      question: "What is a good profit factor?",
      answer: "A profit factor above 1.5 is generally considered acceptable, with 2.0+ being strong. However, context matters significantly. A 1.3 profit factor over 500 trades is more reliable than 2.5 over 20 trades. The 'goodness' of a profit factor also depends on your trading style—high-frequency strategies might target lower PF with more trades, while swing traders might expect higher PF with fewer trades."
    },
    {
      question: "Is profit factor better than win rate?",
      answer: "Neither is 'better'—they measure different things. Win rate tells you how often you win; profit factor tells you how efficiently you win. A 40% win rate with large winners and small losers can have an excellent profit factor. A 70% win rate with small winners and large losers can have a terrible profit factor. You need both metrics together for a complete picture."
    },
    {
      question: "Can profit factor be misleading?",
      answer: "Yes, in several ways: 1) Small sample sizes can show artificially high or low PF that won't persist. 2) A single large winner can inflate PF dramatically. 3) Mixing different strategies or assets masks individual performance. 4) PF doesn't account for drawdown severity or timing. Always consider profit factor alongside other metrics and with sufficient trade volume."
    },
    {
      question: "How many trades are needed for reliable profit factor?",
      answer: "As a general guideline, 50 trades minimum for directional indication, 100+ trades for reasonable confidence, and 200+ trades for high reliability. The key is that profit factor calculated over 20 trades is essentially meaningless—variance dominates at small sample sizes. Professional traders typically want 100-200+ trades before drawing firm conclusions about a strategy's profit factor."
    },
    {
      question: "What's the relationship between profit factor and expectancy?",
      answer: "Both measure profitability but differently. Expectancy tells you the average amount you expect to make per trade. Profit factor tells you the ratio of gross profits to gross losses. A profit factor of 1.5 means for every $1 lost, you made $1.50. Expectancy might be $50/trade. They're complementary—expectancy gives absolute values, profit factor gives efficiency ratio."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Try GENI Free
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 pt-16 pb-20">
        
        <header className="mb-12">
          <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
            <Calculator size={16} weight="fill" />
            <span>Trading Metrics</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            What Is Profit Factor in Trading
            <span className="block text-white/60 text-xl md:text-2xl mt-2">
              (And Why It Matters More Than You Think)
            </span>
          </h1>
          
          <p className="text-lg text-white/70 leading-relaxed">
            Most traders obsess over win rate or total P&L. "I won 70% of my trades!" or "I made 
            $5,000 this month!" But these numbers alone can be misleading. A trader winning 70% 
            of the time can still be losing money if their losses are large enough. And $5,000 
            profit tells you nothing about efficiency or sustainability.
          </p>
        </header>

        {/* Introduction */}
        <section className="mb-14 prose prose-invert max-w-none">
          <p className="text-white/70 leading-relaxed text-lg">
            <strong className="text-white">Profit factor in trading</strong> cuts through the noise. 
            It measures the relationship between your gross profits and gross losses—giving you a 
            single number that reflects the overall quality of your <strong className="text-white">trading 
            performance analysis</strong>. It's one of the most important <strong className="text-white">trading 
            metrics</strong> professionals use to evaluate strategies.
          </p>
          <p className="text-white/70 leading-relaxed text-lg">
            Understanding <strong className="text-white">what is profit factor</strong> and how to 
            use it can transform how you evaluate your own trading—and help you identify what's 
            actually working versus what just feels like it's working.
          </p>
        </section>

        {/* Section 1: What Is Profit Factor */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">01</span>
            What Is Profit Factor (Simple Explanation)
          </h2>
          
          {/* Formula Highlight Box */}
          <div className="bg-gradient-to-br from-[#00E676]/20 to-[#00E676]/5 border-2 border-[#00E676]/40 rounded-xl p-8 mb-8 text-center">
            <p className="text-white/60 text-sm mb-3 uppercase tracking-wider">The Formula</p>
            <div className="text-3xl md:text-4xl font-bold mb-4">
              <span className="text-[#00E676]">Profit Factor</span>
              <span className="text-white/40 mx-3">=</span>
              <span className="text-white">
                <span className="text-green-400">Total Profit</span>
                <span className="text-white/40 mx-2">÷</span>
                <span className="text-red-400">Total Loss</span>
              </span>
            </div>
            <p className="text-white/60 text-sm">
              (using absolute values for losses)
            </p>
          </div>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              In plain terms: profit factor tells you how much money you make for every dollar 
              you lose. A profit factor of 2.0 means for every $1 you lose, you make $2 back. 
              A profit factor of 1.0 means you're breaking even—your profits exactly equal your 
              losses.
            </p>
          </div>
          
          {/* Example Box */}
          <div className="bg-[#121212] border border-white/10 rounded-lg p-6 mb-6">
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Calculator size={18} className="text-[#00E676]" />
              Simple Example
            </h4>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-white/50 text-sm mb-2">Your trading results:</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between">
                    <span className="text-white/70">Total Winning Trades:</span>
                    <span className="text-green-400 font-medium">+$8,500</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-white/70">Total Losing Trades:</span>
                    <span className="text-red-400 font-medium">-$4,250</span>
                  </li>
                </ul>
              </div>
              <div className="bg-[#00E676]/10 rounded-lg p-4">
                <p className="text-white/50 text-sm mb-2">Calculation:</p>
                <p className="text-lg font-mono">
                  $8,500 ÷ $4,250 = <span className="text-[#00E676] font-bold">2.0</span>
                </p>
                <p className="text-white/60 text-xs mt-2">
                  For every $1 lost, you made $2
                </p>
              </div>
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Anything above 1.0 means you're profitable overall. Below 1.0, you're losing money. 
              The higher the profit factor, the more efficiently your winning trades are 
              compensating for your losing trades.
            </p>
          </div>
        </section>

        {/* Section 2: Why Profit Factor Is Important */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">02</span>
            Why Profit Factor Is Important
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Profit factor matters because it captures the quality of your trading in a way 
              that neither win rate nor total P&L can alone. It answers a fundamental question: 
              <strong className="text-white"> how efficiently are you converting risk into reward?</strong>
            </p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {[
              {
                title: "Measures Strategy Quality",
                description: "A high profit factor indicates a robust strategy that generates more profit than loss consistently.",
                icon: ChartLineUp
              },
              {
                title: "Shows Efficiency",
                description: "Reveals how effectively your winners compensate for your losers—beyond just counting wins.",
                icon: Scales
              },
              {
                title: "Evaluates Consistency",
                description: "A stable profit factor over time suggests repeatable edge, not just lucky streaks.",
                icon: ArrowsClockwise
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-[#00E676]/20 rounded-lg p-5"
              >
                <item.icon size={24} weight="duotone" className="text-[#00E676] mb-3" />
                <h4 className="font-semibold mb-2 text-sm">{item.title}</h4>
                <p className="text-white/60 text-xs leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              When analyzing your performance, profit factor helps you compare apples to apples. 
              You can compare different setups, timeframes, or assets using the same metric—something 
              that's harder to do with raw P&L numbers.
            </p>
          </div>
        </section>

        {/* Section 3: What Is a Good Profit Factor */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">03</span>
            What Is a Good Profit Factor?
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              There's no universal "good" number—context matters. But here are general benchmarks 
              most traders and analysts use to evaluate <strong className="text-white">profit factor trading</strong> performance:
            </p>
          </div>
          
          <div className="space-y-3 mb-8">
            {[
              {
                range: "Below 1.0",
                label: "Losing Strategy",
                description: "You're losing more than you make. Time to reassess everything.",
                color: "red",
                emoji: "🔴"
              },
              {
                range: "1.0 – 1.2",
                label: "Break-Even / Marginal",
                description: "Barely profitable. Transaction costs may wipe out gains.",
                color: "orange",
                emoji: "🟠"
              },
              {
                range: "1.2 – 1.5",
                label: "Weak Edge",
                description: "Some edge exists, but vulnerable to variance. Room for improvement.",
                color: "yellow",
                emoji: "🟡"
              },
              {
                range: "1.5 – 2.0",
                label: "Solid Performance",
                description: "Good, sustainable edge. Most successful retail traders fall here.",
                color: "green",
                emoji: "🟢"
              },
              {
                range: "2.0+",
                label: "Strong Edge",
                description: "Excellent efficiency. Verify with large sample size—could be luck.",
                color: "emerald",
                emoji: "💚"
              }
            ].map((item, index) => (
              <div 
                key={index}
                className={`flex items-center gap-4 bg-[#121212] border rounded-lg p-4 ${
                  item.color === 'red' ? 'border-red-500/30' :
                  item.color === 'orange' ? 'border-orange-500/30' :
                  item.color === 'yellow' ? 'border-yellow-500/30' :
                  item.color === 'green' ? 'border-green-500/30' :
                  'border-emerald-500/30'
                }`}
              >
                <span className="text-2xl">{item.emoji}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-bold ${
                      item.color === 'red' ? 'text-red-400' :
                      item.color === 'orange' ? 'text-orange-400' :
                      item.color === 'yellow' ? 'text-yellow-400' :
                      item.color === 'green' ? 'text-green-400' :
                      'text-emerald-400'
                    }`}>{item.range}</span>
                    <span className="text-white/40">—</span>
                    <span className="text-white/80 text-sm">{item.label}</span>
                  </div>
                  <p className="text-white/50 text-xs">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-lg p-6">
            <p className="text-white/80 text-sm leading-relaxed">
              <strong className="text-white">Important context:</strong> A profit factor of 3.0 
              over 15 trades is far less meaningful than 1.6 over 300 trades. Sample size dramatically 
              affects reliability. Be skeptical of very high profit factors—they often don't persist. 
              Learn more about <Link to="/how-many-trades-to-validate-a-strategy" className="text-[#00E676] hover:underline">how many trades you need to validate a strategy</Link>.
            </p>
          </div>
        </section>

        {/* Section 4: Profit Factor vs Win Rate */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">04</span>
            Profit Factor vs Win Rate
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Many traders conflate win rate with profitability. They're not the same. Win rate 
              tells you frequency; profit factor tells you efficiency. Understanding this 
              distinction is crucial for proper <strong className="text-white">trading performance analysis</strong>.
            </p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden mb-8">
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10">
              <div className="p-6">
                <h4 className="font-semibold mb-4 text-red-400 flex items-center gap-2">
                  <TrendDown size={18} />
                  High Win Rate, Losing Money
                </h4>
                <div className="space-y-2 text-sm text-white/60 mb-4">
                  <p>Win rate: 75% (15 wins, 5 losses)</p>
                  <p>Average win: <span className="text-green-400">+$100</span></p>
                  <p>Average loss: <span className="text-red-400">-$400</span></p>
                </div>
                <div className="bg-red-500/10 rounded p-3">
                  <p className="text-xs text-white/50 mb-1">Calculation:</p>
                  <p className="text-sm">Profit: $1,500 | Loss: $2,000</p>
                  <p className="text-sm font-bold text-red-400">PF = 0.75 (Losing!)</p>
                </div>
              </div>
              <div className="p-6">
                <h4 className="font-semibold mb-4 text-green-400 flex items-center gap-2">
                  <TrendUp size={18} />
                  Low Win Rate, Making Money
                </h4>
                <div className="space-y-2 text-sm text-white/60 mb-4">
                  <p>Win rate: 35% (7 wins, 13 losses)</p>
                  <p>Average win: <span className="text-green-400">+$500</span></p>
                  <p>Average loss: <span className="text-red-400">-$150</span></p>
                </div>
                <div className="bg-green-500/10 rounded p-3">
                  <p className="text-xs text-white/50 mb-1">Calculation:</p>
                  <p className="text-sm">Profit: $3,500 | Loss: $1,950</p>
                  <p className="text-sm font-bold text-green-400">PF = 1.79 (Profitable!)</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              The lesson: <strong className="text-white">you can't evaluate performance with win rate 
              alone</strong>. The size of wins versus losses matters just as much—often more. 
              Profit factor captures this relationship directly.
            </p>
          </div>
        </section>

        {/* Section 5: Profit Factor vs Risk Reward Ratio */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">05</span>
            Profit Factor vs Risk Reward Ratio
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Profit factor and <Link to="/improve-risk-reward-ratio" className="text-[#00E676] hover:underline">risk reward ratio</Link> are 
              related but measure different things. Understanding both gives you a complete 
              picture of your trading.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Scales size={18} className="text-[#00E676]" />
                Risk Reward Ratio (R:R)
              </h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                  Measures single trade potential
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                  Planned before entry
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                  Target ÷ Stop = R:R
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                  Forward-looking
                </li>
              </ul>
            </div>
            
            <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Calculator size={18} className="text-purple-400" />
                Profit Factor
              </h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-purple-400 mt-0.5 flex-shrink-0" />
                  Measures overall performance
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-purple-400 mt-0.5 flex-shrink-0" />
                  Calculated after trades
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-purple-400 mt-0.5 flex-shrink-0" />
                  Total Profits ÷ Total Losses
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-purple-400 mt-0.5 flex-shrink-0" />
                  Backward-looking (historical)
                </li>
              </ul>
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              <strong className="text-white">How they connect:</strong> If you consistently achieve 
              good R:R on your trades and maintain reasonable accuracy, your profit factor will 
              be strong. Poor execution of R:R targets (cutting winners, letting losers run) will 
              show up as a deteriorating profit factor—even if your planned R:R was good.
            </p>
          </div>
        </section>

        {/* Section 6: The Importance of Sample Size */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-orange-400 text-lg font-mono">06</span>
            The Importance of Sample Size
          </h2>
          
          <div className="bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/20 rounded-lg p-8 mb-6">
            <p className="text-white/80 leading-relaxed mb-4">
              <strong className="text-white">This cannot be overstated:</strong> profit factor 
              calculated over a small number of trades is statistically meaningless. A few lucky 
              trades can show a PF of 5.0. A few unlucky trades can show 0.5. Neither reflects 
              your true edge.
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              { trades: "10-20", reliability: "Essentially Useless", desc: "Variance dominates. Don't draw any conclusions." },
              { trades: "20-50", reliability: "Directional Only", desc: "Might indicate trend, but highly unreliable." },
              { trades: "50-100", reliability: "Getting Meaningful", desc: "Patterns emerging, but still significant variance." },
              { trades: "100-200", reliability: "Reasonably Reliable", desc: "Can start making decisions based on this data." },
              { trades: "200+", reliability: "High Confidence", desc: "Statistically robust. This is what pros use." }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 bg-[#121212] border border-white/10 rounded-lg p-4"
              >
                <div className="w-20 text-center">
                  <span className="text-lg font-bold text-[#00E676]">{item.trades}</span>
                  <p className="text-white/40 text-xs">trades</p>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm mb-1">{item.reliability}</h4>
                  <p className="text-white/50 text-xs">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Always ask: "How many trades is this profit factor based on?" A strategy showing 
              PF of 1.8 over 300 trades is far more trustworthy than one showing PF of 3.0 over 
              25 trades. The latter will likely regress toward the mean.
            </p>
          </div>
        </section>

        {/* Section 7: Common Mistakes */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-red-400 text-lg font-mono">07</span>
            Common Mistakes When Using Profit Factor
          </h2>
          
          <div className="space-y-4 mb-8">
            {[
              {
                mistake: "Trusting Small Datasets",
                description: "Calculating PF from 15-20 trades and treating it as gospel. At this sample size, you're measuring luck, not edge.",
                icon: Warning
              },
              {
                mistake: "Ignoring Market Conditions",
                description: "A strategy's PF during a bull market doesn't represent performance in sideways or bear conditions. Context matters.",
                icon: ChartBar
              },
              {
                mistake: "Mixing Different Assets",
                description: "Combining BTC, ETH, and altcoin trades into one PF calculation. Each asset behaves differently—aggregate numbers hide important details.",
                icon: Funnel
              },
              {
                mistake: "Not Separating Setups",
                description: "Your breakout trades might have PF of 2.3 while reversals show 0.9. A combined PF of 1.4 hides this critical insight.",
                icon: MagnifyingGlass
              },
              {
                mistake: "Ignoring Time Periods",
                description: "Looking at all-time PF without examining whether it's consistent month-to-month. A high average can mask wild swings.",
                icon: ArrowsClockwise
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-red-500/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-red-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.mistake}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 8: Why Profit Factor Changes Across Markets */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">08</span>
            Why Profit Factor Changes Across Markets
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Your profit factor isn't static—it varies based on what you trade and when. A 
              strategy that works beautifully on BTC might fail completely on low-cap altcoins. 
              This isn't a flaw; it's reality.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {[
              {
                factor: "Liquidity",
                description: "Thin order books mean more slippage, affecting actual R:R achieved and thus profit factor.",
                icon: ChartBar
              },
              {
                factor: "Volatility",
                description: "High volatility can help or hurt PF depending on your strategy. Breakouts love it; mean reversion hates it.",
                icon: Lightning
              },
              {
                factor: "Asset Behavior",
                description: "Different coins have different characteristics. BTC moves differently than a new altcoin.",
                icon: CurrencyCircleDollar
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg p-5"
              >
                <item.icon size={24} weight="duotone" className="text-[#00E676] mb-3" />
                <h4 className="font-semibold mb-2 text-sm">{item.factor}</h4>
                <p className="text-white/60 text-xs leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              This is why tracking profit factor separately for different assets and conditions 
              matters. You might discover your edge only exists on certain instruments—a valuable 
              insight that aggregate numbers would hide.
            </p>
          </div>
        </section>

        {/* Section 9: How to Improve Profit Factor */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">09</span>
            How to Improve Profit Factor
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Improving profit factor comes down to two levers: making your winners bigger or 
              making your losers smaller. Here's how to actually do that:
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              {
                action: "Improve Entry Quality",
                description: "Better entries mean tighter stops (smaller losses when wrong) and more room to target (bigger wins when right). Quality over quantity.",
                icon: Crosshair
              },
              {
                action: "Cut Losses Faster",
                description: "When a trade invalidates, exit. Don't hope. Don't move stops. Accept the loss and preserve capital for better setups.",
                icon: TrendDown
              },
              {
                action: "Let Winners Run",
                description: "Use trailing stops or partial exits. Don't close at 1R when the trade could reach 3R. Your winners need to be big enough to cover losers.",
                icon: TrendUp
              },
              {
                action: "Refine Your Setups",
                description: "Analyze which setups have the best PF and double down on those. Eliminate or fix the setups dragging down your overall number.",
                icon: Gear
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-[#00E676]/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-[#00E676]" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.action}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Learning <Link to="/how-to-analyze-your-trades" className="text-[#00E676] hover:underline">how to analyze your trades</Link> effectively 
              is key to identifying which improvements will have the biggest impact on your 
              profit factor.
            </p>
          </div>
        </section>

        {/* Section 10: The Real Use of Profit Factor */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">10</span>
            The Real Use of Profit Factor
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Profit factor isn't just a number to brag about. Its real value is as a 
              <strong className="text-white"> decision-making tool</strong>. Here's how serious 
              traders actually use it:
            </p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg p-8 mb-8">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Lightbulb size={20} weight="duotone" className="text-[#00E676]" />
              Practical Applications
            </h3>
            <div className="space-y-4">
              {[
                {
                  use: "Comparing Setups",
                  detail: "Which of your setups is most efficient? Breakouts at PF 2.1 vs reversals at PF 1.3 tells you where to focus."
                },
                {
                  use: "Identifying Strengths & Weaknesses",
                  detail: "High PF on trending days, low PF on ranging days? Now you know when to trade more aggressively."
                },
                {
                  use: "Validating Changes",
                  detail: "Made a strategy adjustment? Compare PF before and after (with sufficient sample) to see if it actually helped."
                },
                {
                  use: "Capital Allocation",
                  detail: "Higher PF setups might warrant larger position sizes. Lower PF setups might need smaller exposure."
                }
              ].map((item, index) => (
                <div key={index} className="border-l-2 border-[#00E676]/50 pl-4">
                  <h4 className="font-medium text-sm mb-1">{item.use}</h4>
                  <p className="text-white/60 text-sm leading-relaxed">{item.detail}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Many traders use tools like <strong className="text-white">GENI</strong> to 
              automatically calculate profit factor across different setups and asset groups, 
              making it easier to compare performance and identify what works. But regardless 
              of tools, the principle remains: use profit factor as a lens for continuous 
              improvement, not just a report card.
            </p>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-14">
          <div className="bg-gradient-to-br from-[#00E676]/10 to-transparent border border-[#00E676]/30 rounded-lg p-8">
            <h3 className="text-xl font-bold mb-4">Final Thoughts</h3>
            <p className="text-white/80 leading-relaxed mb-4">
              Understanding your <strong className="text-white">profit factor</strong> is one of the 
              most important steps toward improving your trading performance. It cuts through 
              the noise of win rate and raw P&L to show you the efficiency of your trading—how 
              well your winners compensate for your losers.
            </p>
            <p className="text-white/70 leading-relaxed">
              But remember: profit factor is a tool, not a target. Chase understanding, not a 
              specific number. Track it consistently, analyze it in context, and use it to make 
              better decisions about where to focus your improvement efforts.
            </p>
          </div>
        </section>

        {/* Internal Links */}
        <section className="mb-14">
          <div className="p-4 border-l-2 border-[#00E676] bg-[#00E676]/5 rounded-r-lg">
            <p className="text-white/80 text-sm mb-3">
              <strong>Related Articles:</strong>
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              <Link to="/how-to-analyze-your-trades" className="text-[#00E676] hover:underline text-sm">
                How to Analyze Your Trades →
              </Link>
              <Link to="/how-many-trades-to-validate-a-strategy" className="text-[#00E676] hover:underline text-sm">
                How Many Trades to Validate a Strategy →
              </Link>
              <Link to="/improve-risk-reward-ratio" className="text-[#00E676] hover:underline text-sm">
                Improve Risk Reward Ratio →
              </Link>
              <Link to="/why-traders-fail-to-improve" className="text-[#00E676] hover:underline text-sm">
                Why Traders Fail to Improve →
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles Section */}
        <section className="mb-14">
          <h2 className="text-xl font-semibold mb-6">Related Articles</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/how-to-analyze-your-trades" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">How to Analyze Your Trades</h3>
              <p className="text-white/50 text-sm">Step-by-step guide to reviewing trades like a pro.</p>
            </Link>
            <Link to="/improve-risk-reward-ratio" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Improve Risk Reward Ratio</h3>
              <p className="text-white/50 text-sm">Better R:R through execution and psychology.</p>
            </Link>
            <Link to="/how-many-trades-to-validate-a-strategy" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Validate Your Strategy</h3>
              <p className="text-white/50 text-sm">How many trades you need for reliable data.</p>
            </Link>
            <Link to="/automated-trading-journal" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Automated Trading Journal</h3>
              <p className="text-white/50 text-sm">Track metrics automatically for better insights.</p>
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <Question size={28} weight="duotone" className="text-purple-400" />
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium pr-4">{faq.question}</span>
                  <CaretDown 
                    size={18} 
                    className={`text-white/40 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} 
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-white/60 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Soft CTA */}
        <section>
          <div className="bg-gradient-to-br from-[#00E676]/10 via-[#121212] to-purple-500/10 border border-white/10 rounded-lg p-8 md:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#00E676]/20 flex items-center justify-center">
              <Calculator size={32} weight="duotone" className="text-[#00E676]" />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Track Your Profit Factor
            </h2>
            
            <p className="text-white/70 max-w-xl mx-auto mb-8 leading-relaxed">
              Stop guessing whether your strategy works. Track your profit factor across 
              setups, assets, and time periods to make data-driven improvements.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors"
              >
                Start Tracking Free
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white/10 text-white font-semibold rounded-sm hover:bg-white/20 transition-colors"
              >
                Learn More
              </Link>
            </div>
            
            <p className="text-white/40 text-xs mt-6">
              No credit card required · Free tier available
            </p>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "What Is Profit Factor in Trading (And Why It Matters More Than You Think)",
              "description": "Learn what profit factor is in trading, how to calculate it, and how to use it to improve your trading performance.",
              "author": {
                "@type": "Organization",
                "name": "GENI"
              },
              "publisher": {
                "@type": "Organization",
                "name": "GENI",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://genitradeapp.com/geni-logo.png"
                }
              },
              "datePublished": "2026-01-01",
              "dateModified": new Date().toISOString().split('T')[0],
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://genitradeapp.com/profit-factor-trading"
              }
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </article>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-6 opacity-60"
            />
          </Link>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
            <span>© 2026 GENI</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProfitFactorTrading;
